// src/components/DetailedQuestionReport.js
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../../../../firebaseConfig';
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import QuestionRow from './QuestionRow';


// Helper: Checks answer with numeric tolerance
function isAnswerCorrect(studentInput, dbAnswerString) {
  if (!studentInput || !dbAnswerString) return false;

  const sStr = studentInput.toString().trim().toUpperCase();
  const options = dbAnswerString.split(',').map(s => s.trim().toUpperCase());

  // 1. Exact String Match
  if (options.includes(sStr)) return true;

  // 2. Numeric Check
  const parseVal = (val) => {
    if (val.includes('/')) {
      const [n, d] = val.split('/');
      return parseFloat(n) / parseFloat(d);
    }
    return parseFloat(val);
  };

  const sNum = parseVal(sStr);
  if (isNaN(sNum)) return false;

  return options.some((opt) => {
    const optNum = parseVal(opt);
    // Matches the 0.001 margin you set elsewhere
    return !isNaN(optNum) && Math.abs(sNum - optNum) < 0.01;
  });
}


// Define a sort order for the modules
const moduleOrder = {
  RW_Routing: 1,
  RW_Module_A: 2,
  RW_Module_B: 3,
  Math_Routing: 4,
  Math_Module_A: 5,
  Math_Module_B: 6,
};

// Helper to format module names
const formatModuleName = (name) => {
  return String(name || '').replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
};

// Format correct answer like "A / B" if multiple variants are stored
const formatCorrect = (val) =>
  String(val || '')
    .split(/[,\s|]+/) // Removed '/' from the split list
    .map(t => t.trim().toUpperCase())
    .filter(Boolean)
    .join(', ');

const styles = {
  container: {
    marginTop: '28px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    margin: '0 0 12px 0',
    color: '#111827',
  },
  // --- Subcategory card styles ---
  card: {
    marginBottom: '22px',
    borderRadius: '14px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
    overflow: 'hidden',
  },
  sectionHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #e5e7eb',
    background: 'linear-gradient(90deg, #f9fafb, #fdfdff)',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  sectionSubtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 0 0',
    fontWeight: 400,
  },
  // --- Module styles ---
  moduleSection: {
    marginBottom: '22px',
    borderRadius: '14px',
    border: '1px solid #e5e7eb',
    backgroundColor: '#ffffff',
    boxShadow: '0 10px 30px rgba(15,23,42,0.05)',
    overflow: 'hidden',
  },
  moduleHeader: {
    padding: '10px 16px',
    borderBottom: '1px solid #e5e7eb',
    background: 'linear-gradient(90deg, #f9fafb, #eef2ff)',
  },
  moduleTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  tableWrapper: {
    padding: '4px 12px 12px',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  headerRow: {
    borderBottom: '1px solid #e5e7eb',
    textAlign: 'left',
  },
  headerCell: {
    padding: '8px',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#6b7280',
    whiteSpace: 'nowrap',
  },
  row: {
    borderBottom: '1px solid #f3f4f6',
  },
  cell: {
    padding: '10px 8px',
    color: '#374151',
    whiteSpace: 'nowrap',
  },
  loading: {
    fontSize: '14px',
    color: '#4b5563',
  },
  error: {
    color: '#b91c1c',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '10px',
    padding: '10px 12px',
    fontSize: '13px',
  },

  // --- Modal styles ---
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15, 23, 42, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '18px',
    zIndex: 9999,
  },
  modalCard: {
    width: 'min(960px, 96vw)',
    maxHeight: '86vh',
    background: 'rgba(255,255,255,0.98)',
    borderRadius: 18,
    border: '1px solid rgba(226,232,240,0.9)',
    boxShadow: '0 30px 80px rgba(15, 23, 42, 0.35)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  modalHeader: {
    padding: '12px 14px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    background: 'linear-gradient(90deg, #ffffff, #f8fafc)',
  },
  modalTitle: { margin: 0, fontSize: 14, fontWeight: 900, color: '#0f172a' },
  modalSub: { marginTop: 4, fontSize: 12, color: '#64748b', lineHeight: 1.4 },
  closeBtn: {
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    width: 36,
    height: 36,
    borderRadius: 999,
    cursor: 'pointer',
    fontSize: 16,
    lineHeight: '36px',
    textAlign: 'center',
    color: '#0f172a',
    boxShadow: '0 10px 18px rgba(15,23,42,0.10)',
  },
  modalBody: {
    padding: '14px',
    overflow: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  pillRow: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  pill: {
    fontSize: 12,
    padding: '4px 10px',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    background: '#fff',
    fontWeight: 800,
    color: '#0f172a',
  },
  pillGood: { borderColor: '#86efac', background: '#f0fdf4', color: '#166534' },
  pillBad: { borderColor: '#fecaca', background: '#fef2f2', color: '#991b1b' },
  modalImage: {
    width: '100%',
    maxHeight: '56vh',
    objectFit: 'contain',
    borderRadius: 14,
    border: '1px solid #e5e7eb',
    background: '#f8fafc',
  },
  helperText: { fontSize: 12, color: '#64748b' },
  answerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 10,
  },
  answerCard: {
    border: '1px solid #e5e7eb',
    borderRadius: 14,
    padding: 12,
    background: '#fff',
  },
  answerLabel: { fontSize: 11, fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b' },
  answerValue: { marginTop: 6, fontSize: 14, fontWeight: 900, color: '#0f172a' },
  modalActions: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' },
  linkBtn: {
    fontSize: 13,
    padding: '9px 14px',
    borderRadius: 999,
    border: '1px solid #e5e7eb',
    background: '#ffffff',
    color: '#0f172a',
    fontWeight: 800,
    textDecoration: 'none',
  },
  linkBtnBlue: {
    borderColor: '#bfdbfe',
    background: 'linear-gradient(90deg, #dbeafe, #e0e7ff)',
    color: '#1d4ed8',
  },
};

function DetailedQuestionReport({ studentId, testId, attemptId, showSubcategory = false, diagnosticCollection = "sat_diagnostic_tests" }) {
  const [groupedReport, setGroupedReport] = useState({});
  const [subcatStats, setSubcatStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [activeItem, setActiveItem] = useState(null);
  const [modalImageUrl, setModalImageUrl] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  const isModalOpen = !!activeItem;

  const closeModal = () => {
    setActiveItem(null);
    setModalImageUrl('');
    setModalLoading(false);
  };

  useEffect(() => {
    if (!studentId || !testId) return;

    const fetchReportData = async () => {
      setLoading(true);
      setError('');
      try {
        // 1. Fetch Metadata from Collection
        const qSnapshot = await getDocs(collection(db, diagnosticCollection, testId, "questions"));
        const questionDataMap = new Map();
        qSnapshot.forEach(d => {
          questionDataMap.set(d.data()?.questionId, d.data());
        });

        // 2. Fetch Answers from Report Structure
        const reportRef = doc(db, "diagnostic_results", attemptId || studentId);
        const reportSnap = await getDoc(reportRef);

        if (!reportSnap.exists()) throw new Error("Student report not found.");

        const data = reportSnap.data() || {};
        const allStudentAnswers = data.answers || {};

        // If available, limit rows to only modules the student actually took
        const modulesTaken = new Set(
          Array.isArray(data.pathHistory)
            ? data.pathHistory.map((h) => h?.module).filter(Boolean)
            : []
        );

        // -----------------------------
        // A) BUILD QUESTION ROWS (show skipped)
        // -----------------------------
        // Iterate *all* questions from this test and merge the student's answer.
        const tempGroupedItems = {};
        for (const [questionId, q] of questionDataMap.entries()) {
          // If we know which modules were taken, skip others
          if (modulesTaken.size && q?.module && !modulesTaken.has(q.module)) continue;

          const studentAnsRaw = allStudentAnswers[questionId] ?? "";
          const studentAnswer = String(studentAnsRaw).trim().toUpperCase();

          const isSkipped = studentAnswer === "";
          // USE NEW HELPER
          const isCorrect = !isSkipped && isAnswerCorrect(studentAnswer, q.correctAnswer);

          const item = {
            ...q,
            studentAnswer,
            isCorrect,
            // harmless extra; QuestionRow will ignore if not used
            status: isSkipped ? "Skipped" : isCorrect ? "Correct" : "Incorrect",
          };

          const moduleName = item.module || "Uncategorized";
          if (!tempGroupedItems[moduleName]) tempGroupedItems[moduleName] = [];
          tempGroupedItems[moduleName].push(item);
        }

        // Sort items within each module by question number (preserves your UI)
        for (const moduleName in tempGroupedItems) {
          tempGroupedItems[moduleName].sort(
            (a, b) => (a.questionNumber || 0) - (b.questionNumber || 0)
          );
        }

        // -----------------------------
        // B) SUBCATEGORY BREAKDOWN (unchanged: based on answers only)
        // -----------------------------
        const tempSubcats = {};
        for (const [questionId, ans] of Object.entries(allStudentAnswers)) {
          const q = questionDataMap.get(questionId);
          if (!q) continue;

          const studentAnswer = String(ans || "").trim().toUpperCase();
          const isCorrect = isAnswerCorrect(studentAnswer, q.correctAnswer);

          const unitLabel = q?.category || "Uncategorized";
          const subcatLabel = q?.subcategory || "Uncategorized";
          const subKey = q?.subcategoryKey || subcatLabel.replace(/\W+/g, "_");

          const comboKey = `${unitLabel}|${subKey}`;
          if (!tempSubcats[comboKey]) {
            tempSubcats[comboKey] = {
              unit: unitLabel,
              subcat: subcatLabel,
              correct: 0,
              total: 0,
            };
          }
          tempSubcats[comboKey].total += 1;
          if (isCorrect) tempSubcats[comboKey].correct += 1;
        }

        // Push subcat stats (same sort you already had)
        setSubcatStats(
          Object.values(tempSubcats).sort(
            (a, b) => a.unit.localeCompare(b.unit) || a.subcat.localeCompare(b.subcat)
          )
        );

        // Keep your module ordering logic
        const sortedGroupedReport = Object.keys(tempGroupedItems)
          .sort(
            (a, b) =>
              (moduleOrder[a] || 99) - (moduleOrder[b] || 99) ||
              String(a).localeCompare(String(b))
          )
          .reduce((obj, key) => {
            obj[key] = tempGroupedItems[key];
            return obj;
          }, {});

        setGroupedReport(sortedGroupedReport);
      } catch (err) {
        console.error("Error building report:", err);
        setError(err?.message || String(err));
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [studentId, testId, attemptId, diagnosticCollection]);

  // Fetch image when modal opens
  useEffect(() => {
    const run = async () => {
      if (!isModalOpen) return;
      if (!activeItem?.imagePath) return;

      setModalLoading(true);
      try {
        const storage = getStorage();
        const imageRef = ref(storage, activeItem.imagePath);
        const url = await getDownloadURL(imageRef);
        setModalImageUrl(url);
      } catch (e) {
        console.error("Error fetching modal image:", e);
        setModalImageUrl('');
      } finally {
        setModalLoading(false);
      }
    };
    run();
  }, [isModalOpen, activeItem?.imagePath]);

  // ESC to close
  useEffect(() => {
    if (!isModalOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isModalOpen]);

  const handleOpenModal = (item) => {
    setActiveItem(item);
    setModalImageUrl('');
    setModalLoading(false);
  };

  const modalHeaderSub = useMemo(() => {
    if (!activeItem) return '';
    const bits = [
      activeItem.module ? formatModuleName(activeItem.module) : null,
      activeItem.category || null,
      activeItem.subcategory || null,
      activeItem.difficulty ? String(activeItem.difficulty) : null,
    ].filter(Boolean);
    return bits.join(' • ');
  }, [activeItem]);

  if (loading) return <div style={styles.loading}>Loading detailed report...</div>;
  if (error) return <div style={styles.error}>Error: {error}</div>;

  return (
    <div style={styles.container}>
      <h4 style={styles.title}>Question Breakdown</h4>

      {/* Subcategory Breakdown Table */}
      {showSubcategory && (
        <div style={styles.card}>
          <div style={styles.sectionHeader}>
            <h3 style={styles.sectionTitle}>Subcategory Breakdown</h3>
            <p style={styles.sectionSubtitle}>
              Correct / Total and percent by subcategory (based on this attempt’s answers)
            </p>
          </div>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.headerCell}>Unit</th>
                  <th style={styles.headerCell}>Subcategory</th>
                  <th style={styles.headerCell}>Correct</th>
                  <th style={styles.headerCell}>Total</th>
                  <th style={styles.headerCell}>%</th>
                </tr>
              </thead>
              <tbody>
                {subcatStats.map((row, idx) => {
                  const pct = row.total ? Math.round((100 * row.correct) / row.total) : 0;
                  return (
                    <tr key={`${row.unit}-${row.subcat}-${idx}`} style={styles.row}>
                      <td style={styles.cell}>{row.unit}</td>
                      <td style={styles.cell}>{row.subcat}</td>
                      <td style={styles.cell}>{row.correct}</td>
                      <td style={styles.cell}>{row.total}</td>
                      <td style={styles.cell}>{pct}%</td>
                    </tr>
                  );
                })}
                {!subcatStats.length && (
                  <tr><td style={styles.cell} colSpan={5}>No data yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detailed Question Tables */}
      {Object.entries(groupedReport).map(([moduleName, items]) => (
        <div key={moduleName} style={styles.moduleSection}>
          <div style={styles.moduleHeader}>
            <h5 style={styles.moduleTitle}>{formatModuleName(moduleName)}</h5>
          </div>
          <div style={styles.tableWrapper}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.headerRow}>
                  <th style={styles.headerCell}>Q#</th>
                  <th style={styles.headerCell}>Status</th>
                  <th style={styles.headerCell}>Category</th>
                  <th style={styles.headerCell}>Subcategory</th>
                  <th style={styles.headerCell}>Difficulty</th>
                  <th style={styles.headerCell}>Student&apos;s Answer</th>
                  <th style={styles.headerCell}>Correct Answer</th>
                  <th style={styles.headerCell}>Explanation</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <QuestionRow
                    key={item.questionId}
                    item={item}
                    onOpen={handleOpenModal}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Center modal for question preview */}
      {isModalOpen && activeItem && (
        <div
          style={styles.modalOverlay}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div style={styles.modalCard} role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Question {activeItem.questionNumber}</h3>
                <div style={styles.modalSub}>{modalHeaderSub}</div>
              </div>
              <button style={styles.closeBtn} onClick={closeModal} aria-label="Close">✕</button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.pillRow}>
                <span style={{ ...styles.pill, ...(activeItem.isCorrect ? styles.pillGood : styles.pillBad) }}>
                  {activeItem.isCorrect ? 'Correct' : 'Incorrect'}
                </span>
                {activeItem.questionId && <span style={styles.pill}>{activeItem.questionId}</span>}
                {activeItem.subcategoryKey && <span style={styles.pill}>{activeItem.subcategoryKey}</span>}
              </div>

              {modalLoading && <div style={styles.helperText}>Loading question image…</div>}
              {!!modalImageUrl && (
                <img
                  src={modalImageUrl}
                  alt={`Question ${activeItem.questionNumber}`}
                  style={styles.modalImage}
                />
              )}
              {!modalLoading && !modalImageUrl && (
                <div style={styles.helperText}>No image found for this question.</div>
              )}

              <div style={styles.answerGrid}>
                <div style={styles.answerCard}>
                  <div style={styles.answerLabel}>Your Answer</div>
                  <div style={styles.answerValue}>{String(activeItem.studentAnswer || '').toUpperCase() || '—'}</div>
                </div>
                <div style={styles.answerCard}>
                  <div style={styles.answerLabel}>Correct Answer</div>
                  <div style={styles.answerValue}>{formatCorrect(activeItem.correctAnswer) || '—'}</div>
                </div>
                <div style={styles.answerCard}>
                  <div style={styles.answerLabel}>Difficulty</div>
                  <div style={styles.answerValue}>{activeItem.difficulty || '—'}</div>
                </div>
              </div>

              <div style={styles.modalActions}>
                {activeItem.explanationLink ? (
                  <a
                    href={activeItem.explanationLink}
                    target="_blank"
                    rel="noreferrer"
                    style={{ ...styles.linkBtn, ...styles.linkBtnBlue }}
                  >
                    Open Explanation
                  </a>
                ) : (
                  <span style={styles.helperText}>No explanation link for this question.</span>
                )}
              </div>

              <div style={styles.helperText}>Tip: press <strong>Esc</strong> to close.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DetailedQuestionReport;